import React, { Component } from 'react';
import {
  Box,
  Paper,
  Stack,
  Typography,
  LinearProgress,
  Chip,
  Tooltip,
  CircularProgress,
  Alert,
  AlertTitle,
  Collapse,
  IconButton,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Divider
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import BlockOutlinedIcon from '@mui/icons-material/BlockOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import OpenInNewOutlinedIcon from '@mui/icons-material/OpenInNewOutlined';
import { Link as RouterLink } from 'react-router-dom';
import { getVerdicts } from '../../services/moderationDashboardService';
import { motifLabel, isOutOfTaxonomy } from '../../lib/moderationMotifs';
// Libellés de mode partagés (B2_02) : les mêmes mots que le back-office CLIENT, sur les
// deux onglets et sur la fiche communauté. Avant, cet écran affichait la valeur brute
// (`AI_AND_INTERNAL`) pendant que l'onglet « Config par site » l'affichait en clair.
import { modeLabel } from '../../lib/moderationModes';
// B6_02 — nom lisible d'une consigne (même libellé que l'écran d'édition, décision E).
// ⛔ Ne JAMAIS importer titleOf/GROUPS depuis Agents.jsx : cycle Agents ↔ PromptDiff.
import { consigneLabel } from '../../lib/moderationAgents';

// Onglet « Monitoring » du dashboard Modération IA (B2_01, Volet A).
//
// L'écran qui RÉVÈLE le signal n°1 (EPIC §3) : pas « X commentaires modérés », mais « où et
// dans quel sens l'IA se trompe ». Consomme CD1 (GET /suadmin/moderation-dashboard/verdicts).
//
// ⚠ Rendu SANS AUCUNE PROP par ModerationDashboard.jsx → ce composant porte son propre état
// from/to. B3_01 le remontera plus tard dans le conteneur (sérialisation prévue par COHERENCE).
// ⚠ Class component sans hooks — convention du repo (super-admin/CLAUDE.md). Patron : Funnel.js.
//
// ─────────────────────────────────────────────────────────────────────────────────────────────
// DEUX RÉGIMES, DEUX MÉTRIQUES — JAMAIS FUSIONNÉES (DA12, D12)
//
//   régime « appliqué »   → l'IA a AGI (elle a changé le statut du commentaire). Le signal est
//                           le RENVERSEMENT : un humain est repassé derrière et l'a corrigée.
//   régime « suggestion » → l'IA a PROPOSÉ, sans rien changer. Le signal est le DÉSACCORD :
//                           l'humain a tranché autrement. Il n'y a rien à « renverser ».
//
// Ce ne sont pas deux vues d'une même mesure : additionner un renversement et un désaccord ne
// veut rien dire. D'où deux SECTIONS distinctes, deux vocabulaires distincts, et aucun total
// commun nulle part. CD1 renvoie une ligne par site × RÉGIME — un site basculé le 2026-07-24
// apparaît donc légitimement DEUX FOIS, une ligne par régime.
//
// ─────────────────────────────────────────────────────────────────────────────────────────────
// D3 (gelée) — AUCUNE COURBE. CD1 ne porte aucune dimension temporelle (un seul couple
// window.from/to, des agrégats totaux) : une tendance est littéralement incalculable ici.
// La bascule de régime de juillet 2026 est donc signalée par une MENTION TEXTUELLE, pas par un
// repère sur un graphique. La courbe arrivera en Phase 2, avec le versionnage des prompts.
//
// D10 (gelée) — volume d'analyses, JAMAIS un montant en euros (aucun prix unitaire n'existe).
// Décision de fenêtre (2026-08-27) — l'indicateur « latence » sort du périmètre : AUCUNE
// instrumentation ne mesure la durée d'une analyse dans le pipeline (trou T5). Ne pas
// l'afficher vide, ne pas l'inventer.
//
// Décision de fenêtre (2026-08-27) — BARRES DE PROPORTION, pas de graphiques : la donnée de CET
// écran n'a pas de dimension temporelle (CD1 : un seul couple window.from/to, des agrégats
// totaux), une courbe n'aurait rien à tracer. ⚠ Faits recalés le 2026-09-04 (B8_02) — les
// arguments de coût de la rédaction d'origine sont PÉRIMÉS : `victory` est importé dans src/
// depuis `BlocsAffiches.js:22` (B10_02) puis `FunnelTimeseries.js` (B8_02), les ~74 kB gzip
// sont déjà payés, et le dashboard Funnel porte désormais un graphique quotidien. La décision
// « pas de graphique ICI » reste valide, mais par son motif DONNÉES, plus par le coût du
// bundle. Encodage des deux directions d'erreur : COULEUR + ICÔNE + LIBELLÉ EN CLAIR (jamais
// la couleur seule). Palette validée par le script de la skill `dataviz` (6 checks PASS,
// séparation CVD ΔE 35,7 protan / 29,5 tritan) ; le WARN de contraste de l'ambre est levé par
// construction, chaque barre portant son libellé et sa valeur en toutes lettres.
// ─────────────────────────────────────────────────────────────────────────────────────────────

const DAY_MS = 24 * 60 * 60 * 1000;
// ⛔ Le défaut de fenêtre a DÉMÉNAGÉ dans ModerationDashboard.jsx (B3_01) : la période est
// désormais portée par le conteneur, sinon elle repartait à 90 j à chaque aller-retour
// d'onglet — ce composant est démonté à chaque changement d'onglet.
// ⚠ DAY_MS et toInputDate RESTENT ici : `maturation.relevant` et `windowLabel()` les utilisent.

// Couleurs des deux directions d'erreur. Prises telles quelles dans le thème du repo
// (app/theme.js) — on n'invente pas de teinte hors design system.
//   laxisme     → warning  #F59E0B  (l'IA n'a pas vu)
//   sur-censure → info     #1570EF  (l'IA a bloqué à tort)
const DIRECTION_COLOR = { laxisme: 'warning', sur_censure: 'info' };

// Valeurs machine de CD1. Écrites ici plutôt qu'en dur dans le JSX pour qu'une évolution du
// contrat se voie d'un seul endroit.
const REGIME_APPLIED = 'applied';
const REGIME_SUGGESTED = 'suggested';

// Vocabulaire d'écran, PAR RÉGIME. Le ticket l'impose : « corrigée » (l'IA a agi, on l'a
// reprise) ne se dit pas comme « pas suivie » (l'IA a proposé, on a fait autrement). Deux
// métriques différentes → deux phrases différentes.
const REGIME_UI = {
  [REGIME_APPLIED]: {
    title: 'Régime appliqué — l’IA a agi',
    subtitle:
      'L’IA a elle-même changé le statut du commentaire. Ce qu’on mesure ici est un '
      + 'renversement : un humain est repassé derrière et l’a corrigée.',
    laxisme: 'L’IA a laissé passer, un humain a supprimé ensuite',
    sur_censure: 'L’IA a supprimé, un humain a rétabli ensuite',
    empty:
      'Aucun verdict appliqué sur cette période. Depuis la mise en production de juillet 2026, '
      + 'seuls les sites explicitement armés le 24/07 appliquent les verdicts — partout ailleurs '
      + 'l’IA se contente de suggérer.'
  },
  [REGIME_SUGGESTED]: {
    title: 'Régime suggestion — l’IA a proposé',
    subtitle:
      'L’IA n’a rien changé : elle a proposé un verdict, l’humain a tranché. Ce qu’on mesure ici '
      + 'est un désaccord, pas un renversement — et c’est le signal le plus propre.',
    laxisme: 'L’IA proposait de garder, l’humain a supprimé',
    sur_censure: 'L’IA proposait de supprimer, l’humain a gardé',
    empty:
      'Aucune suggestion sur cette période. ⚠ En production c’est pourtant le régime le plus '
      + 'volumineux : un vide ici sur une période récente est plus probablement un trou de '
      + 'données qu’une absence réelle d’activité.'
  }
};

// YYYY-MM-DD : valeur d'un <input type="date"> ET borne envoyée à l'endpoint (cf. Funnel.js).
function toInputDate(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

const nf = new Intl.NumberFormat('fr-FR');

// Les `rate` de CD1 sont des FRACTIONS 0..1 arrondies à 4 décimales, pas des pourcentages.
// Le plafond à 100 % est purement défensif : par construction laxisme ≤ okDenominator et
// sur_censure ≤ koDenominator, donc un dépassement signalerait une anomalie serveur.
function pct(rate) {
  const v = (Number(rate) || 0) * 100;
  return `${Math.min(100, Math.max(0, v)).toFixed(1).replace('.', ',')} %`;
}

function barValue(rate) {
  return Math.max(0, Math.min(100, (Number(rate) || 0) * 100));
}

// Un site supprimé remonte avec `name: null` (CD1) → on retombe sur l'identifiant plutôt que
// d'afficher une case vide qu'on ne saurait pas rattacher.
function siteLabel(row) {
  return row.name || `(site supprimé · ${row.website_id})`;
}

// Dénominateurs de désaccord — RÉPLIQUE du calcul serveur, au niveau MODULE.
//
// ⛔ POURQUOI ICI ET PAS DANS LA CLASSE. L'écran ET l'export CSV doivent produire exactement
// le même verdict sur la fiabilité d'un taux. Deux implémentations divergeraient au premier
// ajustement, et le fichier envoyé au prestataire dirait autre chose que l'écran — ce que la
// décision T1 (2026-08-27) interdit. Une fonction, deux consommateurs : la classe et
// buildExportRows().
function computeDenominators(row) {
  const v = (row && row.verdicts) || {};
  const d = (row && row.disagreement) || {};
  // D28 (B1_04) — le dénominateur est le nombre TOTAL de propositions de l'IA, dans les deux
  // régimes : plus de soustraction des suggestions jamais tranchées (elles comptent comme des
  // accords tacites et restent au dénominateur). Les `Math.max(0, …)` sont partis avec elle :
  // ce sont des compteurs, un bornage n'a plus d'objet.
  const laxisme = Number(v.OK) || 0;
  const sur_censure = Number(v.KO) || 0;
  const serverDenominator = Number(d.denominator);
  const replicaMatches = !Number.isFinite(serverDenominator)
    || (laxisme + sur_censure) === serverDenominator;
  return { laxisme, sur_censure, replicaMatches, serverDenominator };
}

// CD1+ (B1_04) — la ventilation `pending_ok` / `pending_ko` est OBLIGATOIRE côté API (0 en régime
// appliqué). Mais une api d'AVANT B1_04 ne la sert pas, et une clé ABSENTE n'est pas un 0 :
// pendant la fenêtre de MEP (écran déployé avant l'api), on n'affiche que le total — jamais
// « (0 garder · 0 supprimer) » ni deux colonnes CSV qui contrediraient la troisième.
function hasPendingVentilation(d) {
  return !!d
    && d.pending_ok !== undefined && d.pending_ok !== null
    && d.pending_ko !== undefined && d.pending_ko !== null;
}

// LES TROIS ÉTATS D'UNE DIRECTION D'ERREUR, en un seul endroit.
//   'measurable'   → cas ≤ dénominateur et dénominateur > 0 : le taux vaut quelque chose
//   'unmeasurable' → rien au dénominateur : RIEN À MESURER, surtout pas « 0 % »
//   'inconsistent' → cas > dénominateur : N cas RÉELS, taux incalculable
// ⚠ Le 3ᵉ est un FILET contre une réponse serveur malformée : depuis D28 (B1_04) le numérateur
// est par construction un sous-ensemble du dénominateur, donc cet état est injoignable en
// nominal. Il RESTE — le supprimer « puisqu'il ne sert plus » retirerait la seule protection
// contre un serveur qui annoncerait moins de verdicts que de cas.
function directionState(entry, denominator) {
  const count = Number(entry && entry.count) || 0;
  if (count > denominator) return { state: 'inconsistent', count };
  if (entry && denominator > 0) return { state: 'measurable', count };
  return { state: 'unmeasurable', count };
}

// Mention affichée à la place d'un taux qui n'en est pas un. Reprise MOT POUR MOT dans le CSV.
const RATE_UNMEASURABLE = 'non mesurable';
const RATE_INCONSISTENT = 'taux non calculable';

// D28 (B1_04, décisions Mathias Q2/Q3 du 2026-08-28) — la réserve du régime suggestion et la
// mention de comparabilité, reprises MOT POUR MOT dans le CSV (T1 : le fichier dit ce que dit
// l'écran). ⚠ METHOD_CHANGE_LABEL : identifie la version de la méthode ; à ajuster à la date de
// MEP si l'on préfère une date à la référence de décision.
const METHOD_CHANGE_LABEL = 'D28, version d’août 2026';
const SUGGESTION_FLOOR_WARNING = '⚠ En régime « suggestion », les propositions que personne n’a tranchées comptent comme des accords tacites : le taux de désaccord est un plancher — le taux réel est au moins celui affiché. Les NOMBRES DE CAS, eux, sont justes.';
const METHOD_CHANGE_NOTICE = `⚠ Méthode de calcul modifiée (${METHOD_CHANGE_LABEL}) : le dénominateur est désormais le total des propositions de l’IA. Les exports produits avant cette version ne sont pas comparables.`;

// ─────────────────────────────────────────────────────────────────────────────────────────────
// B6_02 — VENTILATION PAR VERSION DE CONSIGNE (CD13)
//
// Fonctions PURES de module, partagées écran ↔ CSV (même discipline T1 que computeDenominators).

// Une entrée `by_version` ne porte PAS la clé `regime` : elle porte `applied_regime` (le
// régime de la ligne parente, applied/suggested) et `agent_mode` (strict/doubt — un AUTRE
// sens du mot « régime »). Les consommateurs partagés testent `row.regime` → remappage
// explicite. ⛔ JAMAIS `agent_mode` ici : ni `strict` ni `doubt` n'égalent 'suggested',
// toute la ventilation pending_* par version s'éteindrait en silence (C2).
function versionRow(entry) {
  return { ...entry, regime: entry.applied_regime };
}

// Garde-fou d'AFFICHAGE (décision D, 2026-08-31) — PAS un test statistique, et nommé comme
// tel à l'écran. Un écart entre deux versions ne s'affiche que si CHACUNE porte au moins
// 2 analyses ; le taux global de la ligne, lui, s'affiche dès le 1ᵉʳ verdict (B1_04/Q1) —
// asymétrie assumée et dite (VERSION_FLOOR_NOTE).
const VERSION_FLOOR = 2;
const VERSION_FLOOR_NOTE = 'Un taux se lit dès la première analyse ; un écart entre deux versions demande au moins 2 analyses de chaque côté — garde-fou d’affichage, pas un test statistique.';

// Décision F (2026-08-31) — `agent_version: null` a DEUX causes que le payload actuel ne
// distingue pas (registre non relu à l'estampillage, OU plusieurs numéros fournisseur après
// une resynchronisation C10 — le drapeau interne n'est pas sérialisé). Libellé unique PRUDENT.
// ⛔ Jamais « non relu » ici : ce libellé est réservé à `mistral_version: null` (D53).
const AGENT_VERSION_UNKNOWN = 'indéterminé';
const AGENT_VERSION_UNKNOWN_TOOLTIP = 'Numéro fournisseur au moment du verdict indéterminé : soit le registre n’avait pas encore été relu à l’estampillage, soit plusieurs numéros fournisseur cohabitent pour cette version (après une resynchronisation faite hors de l’outil). La donnée servie ne distingue pas les deux cas.';
const MISTRAL_VERSION_UNVERIFIED = 'n° fournisseur non relu'; // D53
const HUMAN_DECISIONS_UNMEASURABLE = 'non mesurable : un accord ne laisse pas de trace'; // D56

// D59 + décision E — l'âge d'une version est calculé PAR L'API à la borne `to` de la période
// (`published_since_days`, valeur SIGNÉE, null quand la ligne de registre est introuvable).
// QUATRE libellés : un négatif dit « publiée après la borne » — jamais un nombre négatif brut,
// et 0 signifie « le jour de la borne », donc DANS la période.
function publishedSinceLabel(days, toLabel) {
  if (days === null || days === undefined) return 'date de publication inconnue';
  const n = Number(days);
  if (n < 0) return 'publiée après la fin de la période affichée';
  if (n === 0) return 'publiée le dernier jour de la période';
  return toLabel ? `au ${toLabel} : ${nf.format(n)} jours` : `${nf.format(n)} jours`;
}

// Libellés de refus d'un écart — repris MOT POUR MOT dans le CSV (T1).
const GAP_REFUSED_ADOPTED = 'date exacte du changement inconnue — écart non calculable';
const GAP_REFUSED_RATE = 'écart non calculable (un des deux taux ne l’est pas)';
function gapRefusedVolume(a, b) {
  return `volume insuffisant pour comparer (${nf.format(a)} et ${nf.format(b)} analyses)`;
}

// Écart en POINTS de pourcentage, signé, sans jugement (§3 du ticket : ni couleur de verdict,
// ni flèche — « le jugement appartient au lecteur »).
function gapLabel(recentRate, previousRate) {
  const delta = ((Number(recentRate) || 0) - (Number(previousRate) || 0)) * 100;
  let text = delta.toFixed(1);
  if (text === '-0.0') text = '0.0'; // un arrondi à zéro n'a pas de signe
  const sign = Number(text) > 0 ? '+' : '';
  return `${sign}${text.replace('.', ',')} pts`;
}

// D35 — rapprocher les DEUX dernières versions d'une MÊME consigne. Le tri servi
// (`prompt_version` décroissant, tie-break `agent_id`) ENTRELACE les agents : regrouper par
// `agent_id` d'abord, puis prendre les deux premières de chaque groupe (l'ordre décroissant
// est préservé à l'intérieur d'un groupe). ⛔ Prendre by_version[0]/[1] rapprocherait deux
// consignes différentes — exactement ce que D35 interdit.
function versionComparisons(entries) {
  const byAgent = new Map();
  // Une version SANS ligne de registre (cas 9) sort du rapprochement — elle n'a ni date ni
  // origine : un « avant/après » contre elle n'a pas de frontière du tout, même inconnue.
  // Elle reste une LIGNE du tableau (jamais masquée), seulement hors comparaison.
  (Array.isArray(entries) ? entries : []).filter((e) => !registryMissing(e)).forEach((e) => {
    const list = byAgent.get(e.agent_id) || [];
    list.push(e);
    byAgent.set(e.agent_id, list);
  });
  const out = [];
  byAgent.forEach((list) => {
    if (list.length >= 2) out.push(compareVersions(list[0], list[1]));
  });
  return out;
}

// L'écart entre deux versions — ou son REFUS, avec sa raison, dans cet ordre de précédence :
//   1. 'adopted' (décision C) — une des deux est une remise à niveau (`origin:'adopted'`) :
//      la date réelle du changement est inconnue (réserve D25), la frontière est fantôme.
//   2. volume (décision D) — une des deux porte moins de VERSION_FLOOR analyses.
//   3. par direction — un des deux taux n'est pas mesurable, ou la réplique du dénominateur
//      diverge : l'écran refuse A FORTIORI la soustraction pour cette direction.
// Les taux servis de chaque version restent affichés dans le tableau : seul l'écart est refusé.
function compareVersions(recent, previous) {
  const base = { recent, previous };
  if (recent.origin === 'adopted' || previous.origin === 'adopted') {
    return { ...base, refusal: GAP_REFUSED_ADOPTED, refusalKind: 'adopted' };
  }
  const va = Number(recent.analyses) || 0;
  const vb = Number(previous.analyses) || 0;
  if (va < VERSION_FLOOR || vb < VERSION_FLOOR) {
    return { ...base, refusal: gapRefusedVolume(va, vb), refusalKind: 'volume' };
  }
  const denR = computeDenominators(recent);
  const denP = computeDenominators(previous);
  const directions = {};
  ['laxisme', 'sur_censure'].forEach((dir) => {
    const dR = (recent.disagreement || {})[dir];
    const dP = (previous.disagreement || {})[dir];
    const okR = denR.replicaMatches !== false
      && directionState(dR, denR[dir]).state === 'measurable';
    const okP = denP.replicaMatches !== false
      && directionState(dP, denP[dir]).state === 'measurable';
    directions[dir] = okR && okP
      ? { state: 'gap', label: gapLabel(dR && dR.rate, dP && dP.rate) }
      : { state: 'refused', label: GAP_REFUSED_RATE };
  });
  return { ...base, refusal: null, directions };
}

// Résumé texte d'un rapprochement — partagé écran ↔ CSV (T1 : le fichier dit ce que dit
// l'écran). Porte TOUJOURS les deux volumes (D35/D32 : « avec leurs volumes respectifs »).
function comparisonText(cmp) {
  const ours = (e) => `n° ${e.prompt_version === null || e.prompt_version === undefined ? 'inconnu' : e.prompt_version}`;
  const head = `${ours(cmp.recent)} vs ${ours(cmp.previous)}`;
  const volumes = `(${nf.format(Number(cmp.recent.analyses) || 0)} et ${nf.format(Number(cmp.previous.analyses) || 0)} analyses)`;
  if (cmp.refusal) {
    // Le refus « volume » porte déjà les deux nombres — ne pas les dupliquer.
    return cmp.refusalKind === 'volume' ? `${head} : ${cmp.refusal}` : `${head} : ${cmp.refusal} ${volumes}`;
  }
  return `${head} : laxisme ${cmp.directions.laxisme.label} · sur-censure ${cmp.directions.sur_censure.label} ${volumes}`;
}

// Les DEUX numéros fournisseur s'affichent, avec des libellés distincts (D35, précisé en
// durcissement) : `agent_version` = figé à l'instant du verdict (stamp), `mistral_version` =
// courant de la ligne de registre (réécrit en place par C10). Ils divergent précisément dans
// le scénario que D35 existe pour révéler → signalé quand ils diffèrent.
function agentVersionCell(entry) {
  return entry.agent_version === null || entry.agent_version === undefined
    ? AGENT_VERSION_UNKNOWN
    : String(entry.agent_version);
}
function mistralVersionCell(entry) {
  if (entry.mistral_version !== null && entry.mistral_version !== undefined) {
    return String(entry.mistral_version);
  }
  // « non relu » (D53) suppose une ligne de registre EXISTANTE jamais vérifiée — quand la
  // ligne est introuvable (cas 9), le dire, pas prétendre qu'elle n'a pas été relue.
  return registryMissing(entry) ? 'registre introuvable' : MISTRAL_VERSION_UNVERIFIED;
}
function supplierVersionsDiverge(entry) {
  return typeof entry.agent_version === 'number'
    && typeof entry.mistral_version === 'number'
    && entry.agent_version !== entry.mistral_version;
}

// Cas 9 (jointure à vide) — une version présente dans les verdicts et ABSENTE du registre :
// le contrôleur ne joint que des lignes `published_at ≠ null`, donc `origin === null` ⇔
// registre introuvable (et published_at/author/mistral_version/published_since_days null).
function registryMissing(entry) {
  return entry.origin === null || entry.origin === undefined;
}
const REGISTRY_MISSING_LABEL = 'ligne de registre introuvable pour cette version';

// ─────────────────────────────────────────────────────────────────────────────────────────────
// EXPORT CSV — le fichier recopie les avertissements de l'écran (décision Mathias T1, 2026-08-27)
//
// ⛔ NE JAMAIS ÉCRIRE `disagreement.*.rate` BRUT. L'écran distingue trois états de fiabilité
// (mesurable / non mesurable / incohérent) et refuse le pourcentage dès que la réplique du
// dénominateur diverge du serveur ; un `rate` brut produirait « 0,0 % » dans un tableur là où
// l'écran dit « non mesurable » ou « taux non calculable ». (Jusqu'à D28 / B1_04, l'API
// renvoyait couramment `{count: 4, rate: 0}` avec un dénominateur inférieur au nombre de cas —
// ce cas n'existe plus, le garde-fou reste.) Ce fichier est justement celui qu'on ouvre en
// réunion et qu'on envoie au prestataire : l'analyse fausse hors outil est exactement ce que
// DA12 interdit.
//
// ⛔ VOCABULAIRE DU SUIVI, PAS DE CONFIG (décision T2). La colonne dit « appliqué » /
// « suggestion ». `regimeLabel()` de lib/moderationModes est INTERDIT ici : il rend
// « applique les verdicts » / « suggère seulement », qui sont par D15 les mots de l'onglet
// « Config par site » (le réglage d'AUJOURD'HUI). Les mélanger réimporterait la confusion que
// D15 existe pour supprimer. `modeLabel()`, lui, est le bon outil pour la colonne de mode.
//
// ⚠ Granularité site × régime (D11), jamais le motif : `by_motif[]` ne porte que
// {motif_id, ko, reversed} — ni direction, ni dénominateur. Un fichier « par motif » aurait
// des colonnes vides là où l'écran affiche des taux complets.
//
// Fonction PURE et exportée : c'est le seul oracle testable de ces trois états.
// `gc-recette-ui` n'a aucun verbe de téléchargement — un clic ne prouve que l'absence de
// crash, jamais le contenu du fichier.

const REGIME_CSV_LABEL = {
  [REGIME_APPLIED]: 'appliqué',
  [REGIME_SUGGESTED]: 'suggestion',
};

// Une cellule de taux : le chiffre s'il vaut quelque chose, sinon la mention de l'écran.
//
// ⛔ `replicaMatches === false` REFUSE le taux, comme l'écran. Ce témoin dit que notre réplique
// des dénominateurs a divergé de celle du serveur — donc que les pourcentages de la ligne ne
// veulent plus rien dire. L'écran pose déjà l'avertissement ; sans ce test, l'export serait le
// SEUL chemin par lequel un taux sort du fichier alors que l'écran le déclare non fiable.
function rateCell(entry, denominator, replicaMatches) {
  if (replicaMatches === false) return RATE_INCONSISTENT;
  const { state } = directionState(entry, denominator);
  if (state === 'measurable') return pct(entry && entry.rate);
  if (state === 'inconsistent') return RATE_INCONSISTENT;
  return RATE_UNMEASURABLE;
}

export function buildExportRows(data, options) {
  const opts = options || {};
  const d = data || {};
  const win = d.window || {};
  const unclassified = d.unclassified || {};
  const maturation = d.maturation || {};
  const notes = d.notes || {};
  const rows = [];

  // Bloc d'en-tête (T1) — l'encart d'honnêteté de l'écran doit SUIVRE le fichier hors de
  // l'outil. Les sections sont séparées par une ligne vide, mécanisme natif de rowsToCSV.
  rows.push(['Dashboard Modération IA — suivi des désaccords']);
  rows.push(['Période', opts.from || win.from || '', 'au', opts.to || win.to || '']);
  rows.push(['Date de référence de la fenêtre', win.basis || '(non précisée)']);
  rows.push(['Granularité', 'une ligne par site et par régime (le détail par motif reste à l’écran)']);
  if (Number.isFinite(Number(maturation.reversal_window_days))) {
    rows.push(['Délai de renversement retenu (jours)', maturation.reversal_window_days]);
    // ⛔ SANS CETTE LIGNE, le fichier annonce un sursis de 30 j sans dire à QUEL régime il
    // s'applique : l'écran ne pose ce badge que sur `regime === maturation.applies_to`, un
    // lecteur du CSV l'appliquerait aux lignes « suggestion », où il n'a aucun sens.
    if (maturation.applies_to) {
      rows.push(['… ne s’applique qu’au régime', REGIME_CSV_LABEL[maturation.applies_to] || maturation.applies_to]);
    }
  }
  if (Number.isFinite(Number(notes.mass_action_threshold))) {
    rows.push(['Seuil de geste de masse exclu', notes.mass_action_threshold]);
  }
  if (Number(unclassified.total) > 0) {
    rows.push(['Analyses non classables (hors de ce fichier)', unclassified.total, unclassified.reason || '']);
  }
  rows.push(['⚠ Un taux marqué « ' + RATE_INCONSISTENT + ' » ou « ' + RATE_UNMEASURABLE + ' » n’est PAS un 0 % : se fier au nombre de cas.']);
  // ⛔ L'avertissement CENTRAL de l'écran (renderHonestyNotes) doit suivre le fichier : en
  // régime suggestion le pourcentage est un PLANCHER même quand il est calculable (D28). Le
  // limiter aux taux refusés laisserait sortir sans réserve un taux `measurable` sur une ligne
  // suggestion — dans le fichier même qu'on envoie au prestataire.
  rows.push([SUGGESTION_FLOOR_WARNING]);
  // Q3 (2026-08-28) — un fichier d'avant D28 et un fichier d'après ne se comparent pas : le
  // dénominateur a changé de sens. La mention identifie les fichiers de la nouvelle méthode.
  rows.push([METHOD_CHANGE_NOTICE]);
  // B6_02 — les mentions d'honnêteté servies par l'API (CD13) suivent dans le fichier,
  // VERBATIM : la donnée porte le texte, le fichier le recopie — jamais reformulé en dur.
  // ⛔ Liste OUVERTE (toute clé textuelle), pas une liste blanche : B4_04 ajoute déjà une
  // 6ᵉ note (`unresolved_scope`) — une liste fermée la ferait disparaître en silence.
  Object.keys(notes).forEach((k) => {
    if (typeof notes[k] === 'string' && notes[k]) rows.push([notes[k]]);
  });
  rows.push([]);

  rows.push([
    'Site', 'Identifiant', 'Régime', 'Mode réglé aujourd’hui', 'Analyses',
    'Gardés (OK)', 'Supprimés (KO)', 'Sans décision (REVIEW)',
    'Laxisme — cas', 'Laxisme — base', 'Laxisme — taux',
    'Sur-censure — cas', 'Sur-censure — base', 'Sur-censure — taux',
    'Suggestions jamais tranchées', 'Suggestions jamais tranchées — garder',
    'Suggestions jamais tranchées — supprimer', 'Verdicts reconstitués', 'Suppressions sans motif',
    // B6_02 — les 4 catégories écartées de la ventilation par version (D34), par ligne
    // site × régime : c'est leur dimension, pas celle des tables par version.
    'À cheval sur deux versions', 'Antérieures au suivi par version',
    'Version non résolue', 'Jugés deux fois',
  ]);

  (Array.isArray(d.by_site) ? d.by_site : []).forEach((row) => {
    const v = row.verdicts || {};
    const dis = row.disagreement || {};
    const den = computeDenominators(row);
    rows.push([
      siteLabel(row),
      row.website_id || '',
      REGIME_CSV_LABEL[row.regime] || row.regime || '',
      modeLabel(row.llm_ai_type),
      Number(row.analyses) || 0,
      Number(v.OK) || 0,
      Number(v.KO) || 0,
      Number(v.REVIEW) || 0,
      Number(dis.laxisme && dis.laxisme.count) || 0,
      // Q1-bis (2026-08-28) — la BASE du taux part avec lui : le fichier disait « 66,7 % »
      // sans jamais dire « sur combien ».
      den.laxisme,
      rateCell(dis.laxisme, den.laxisme, den.replicaMatches),
      Number(dis.sur_censure && dis.sur_censure.count) || 0,
      den.sur_censure,
      rateCell(dis.sur_censure, den.sur_censure, den.replicaMatches),
      // `pending_*` n'a de sens qu'en suggestion : l'endpoint les sert à 0 ailleurs (CD1+).
      row.regime === REGIME_SUGGESTED ? (Number(dis.pending_human) || 0) : '',
      row.regime === REGIME_SUGGESTED && hasPendingVentilation(dis) ? (Number(dis.pending_ok) || 0) : '',
      row.regime === REGIME_SUGGESTED && hasPendingVentilation(dis) ? (Number(dis.pending_ko) || 0) : '',
      Number(row.verdict_fallbacks) || 0,
      Number(row.ko_without_motif) || 0,
      // Garde de MEP : une api d'avant B6_01 ne sert pas ces seaux — cellule vide, jamais un
      // 0 qui affirmerait « aucune analyse écartée » sur une donnée absente.
      row.straddling ? (Number(row.straddling.count) || 0) : '',
      row.unstamped ? (Number(row.unstamped.count) || 0) : '',
      row.unresolved ? (Number(row.unresolved.count) || 0) : '',
      row.rejudged ? (Number(row.rejudged.count) || 0) : '',
    ]);
  });

  // ── B6_02 — tables « par version » (décision B, 2026-08-31 : TROIS tables). Gardées par la
  // présence de la clé CD13 : une api d'avant B6_01 ne la sert pas, le fichier garde alors
  // exactement sa forme d'avant. ⚠ Le témoin D11 (une ligne par site × régime en table 1)
  // tient parce que le helper de test `dataRows` est borné à la première ligne vide (C5).
  if (Array.isArray(d.by_version_global)) {
    // T1 — la borne d'âge est celle que le SERVEUR a réellement appliquée (`window.to`) :
    // `published_since_days` est calculé contre elle, jamais contre la saisie locale.
    // MÊME dérivation que windowToLabel() à l'écran (toInputDate) — un `slice(0,10)` sur
    // l'ISO divergerait d'un jour de l'écran sur un fuseau négatif. `opts.to` = repli.
    const winTo = win.to ? new Date(win.to) : null;
    const toLabel = (winTo && !Number.isNaN(winTo.getTime()) ? toInputDate(winTo) : null)
      || opts.to || '';
    const versionHeader = [
      'Consigne', 'Notre n°', 'N° fournisseur (au verdict)', 'N° fournisseur (registre)',
      'Origine', 'Publiée le', 'Âge', 'Auteur', 'Décisions humaines', 'Analyses',
      'Suggestions jamais tranchées',
      'Laxisme — cas', 'Laxisme — base', 'Laxisme — taux',
      'Sur-censure — cas', 'Sur-censure — base', 'Sur-censure — taux',
      'Écart vs version précédente',
    ];
    // La colonne « Écart » n'est portée que par la ligne de la version la plus RÉCENTE de
    // chaque consigne (rapprochement D35) ; les refus (adopted / volume / taux) y sont
    // recopiés tels quels — mêmes mots que l'écran (T1).
    const gapIndex = (entries) => {
      const map = new Map();
      versionComparisons(entries).forEach((cmp) => {
        map.set(`${cmp.recent.agent_id}|${cmp.recent.prompt_version}`, comparisonText(cmp));
      });
      return map;
    };
    const versionCells = (entry, gaps) => {
      const den = computeDenominators(entry);
      const dis = entry.disagreement || {};
      return [
        consigneLabel(entry),
        entry.prompt_version === null || entry.prompt_version === undefined ? '' : entry.prompt_version,
        agentVersionCell(entry),
        // D35 — la divergence des deux numéros est l'information de valeur : dite en toutes
        // lettres dans le fichier, jamais portée par une seule icône d'écran.
        supplierVersionsDiverge(entry)
          ? `${mistralVersionCell(entry)} (divergent du n° au verdict)`
          : mistralVersionCell(entry),
        registryMissing(entry)
          ? REGISTRY_MISSING_LABEL
          : (entry.origin === 'adopted'
            ? 'remise à niveau (date exacte du changement inconnue)'
            : (entry.origin === 'ui' ? 'publiée depuis l’outil' : entry.origin)),
        entry.published_at ? String(entry.published_at).slice(0, 10) : 'inconnue',
        publishedSinceLabel(entry.published_since_days, toLabel),
        entry.author && entry.author.username ? entry.author.username : 'inconnu',
        entry.human_decisions === null || entry.human_decisions === undefined
          ? HUMAN_DECISIONS_UNMEASURABLE : (Number(entry.human_decisions) || 0),
        Number(entry.analyses) || 0,
        // Remappage C2 : le régime vient d'`applied_regime` — jamais d'`agent_mode`.
        versionRow(entry).regime === REGIME_SUGGESTED ? (Number(dis.pending_human) || 0) : '',
        Number(dis.laxisme && dis.laxisme.count) || 0,
        den.laxisme,
        rateCell(dis.laxisme, den.laxisme, den.replicaMatches),
        Number(dis.sur_censure && dis.sur_censure.count) || 0,
        den.sur_censure,
        rateCell(dis.sur_censure, den.sur_censure, den.replicaMatches),
        gaps.get(`${entry.agent_id}|${entry.prompt_version}`) || '',
      ];
    };

    // Table 2 — synthèse toutes communautés (décision A : une par régime, ordre de l'écran :
    // suggestion d'abord). ⛔ Compteurs de `global_by_regime` en NOMBRES NUS — jamais `.count`.
    rows.push([]);
    rows.push(['Synthèse toutes communautés — détail par version de consigne (servie par l’API, jamais recalculée)']);
    const globalByRegime = Array.isArray(d.global_by_regime) ? d.global_by_regime : [];
    [REGIME_SUGGESTED, REGIME_APPLIED].forEach((regime) => {
      const g = globalByRegime.find((r) => r.applied_regime === regime) || {};
      rows.push([
        `Régime ${REGIME_CSV_LABEL[regime]}`, 'analyses', Number(g.analyses) || 0,
        'à cheval sur deux versions', Number(g.straddling) || 0,
        'antérieures au suivi', Number(g.unstamped) || 0,
        'version non résolue', Number(g.unresolved) || 0,
        'jugées deux fois', Number(g.rejudged) || 0,
      ]);
    });
    rows.push(['Régime', ...versionHeader]);
    [REGIME_SUGGESTED, REGIME_APPLIED].forEach((regime) => {
      const entries = d.by_version_global.filter((e) => e.applied_regime === regime);
      const gaps = gapIndex(entries);
      entries.forEach((entry) => {
        rows.push([REGIME_CSV_LABEL[regime], ...versionCells(entry, gaps)]);
      });
    });

    // Table 3 — détail par version et par communauté (D27 : « sous le tableau actuel »).
    rows.push([]);
    rows.push(['Détail par version de consigne, par communauté']);
    rows.push(['Site', 'Identifiant', 'Régime', ...versionHeader]);
    (Array.isArray(d.by_site) ? d.by_site : []).forEach((row) => {
      const entries = Array.isArray(row.by_version) ? row.by_version : [];
      const gaps = gapIndex(entries);
      entries.forEach((entry) => {
        rows.push([
          siteLabel(row), row.website_id || '', REGIME_CSV_LABEL[row.regime] || row.regime || '',
          ...versionCells(entry, gaps),
        ]);
      });
    });
  }

  return rows;
}

class Monitoring extends Component {
  constructor(props) {
    super(props);
    // ⚠ La fenêtre n'est plus un état LOCAL (B3_01) : elle arrive en props depuis le conteneur
    // (`appliedFrom`/`appliedTo` = la fenêtre réellement interrogée). Ce composant est démonté à
    // chaque changement d'onglet ; y garder la période la faisait repartir à 90 j au retour.
    this.state = {
      data: null,
      isLoading: false,
      error: null,
      // DEUX espaces de clés cohabitent (B6_02) : `${website_id}|${regime}` → détail par
      // motif, `version|${website_id}|${regime}` → détail par version. ⛔ Le préfixe est
      // OBLIGATOIRE : une clé partagée ouvrirait/fermerait les deux dépliages ensemble.
      expanded: {}
    };
    this.fetchVerdicts = this.fetchVerdicts.bind(this);
    this.notifyState = this.notifyState.bind(this);
    this.toggleRow = this.toggleRow.bind(this);
  }

  componentDidMount() {
    this._isMounted = true;
    this.fetchVerdicts();
  }

  // ⛔ RECHARGEMENT SUR SIGNAL D'APPLICATION, jamais sur la saisie.
  //
  // Le conteneur porte DEUX couples : `from`/`to` (ce que l'utilisateur tape) et
  // `appliedFrom`/`appliedTo` (la fenêtre réellement interrogée). Comparer la saisie
  // déclencherait un appel CD1 À CHAQUE FRAPPE dans le champ date, et détruirait le couple
  // « je saisis / je valide » que B2_01 avait posé délibérément.
  //
  // `reloadToken` couvre le cas que les deux couples ne peuvent pas exprimer : « Rafraîchir »
  // sur une période INCHANGÉE. Sans lui, le bouton serait sans effet — les bornes n'ayant pas
  // bougé, aucune comparaison ne détecterait la demande.
  componentDidUpdate(prevProps) {
    const windowChanged = prevProps.appliedFrom !== this.props.appliedFrom
      || prevProps.appliedTo !== this.props.appliedTo;
    const refreshAsked = prevProps.reloadToken !== this.props.reloadToken;
    if (windowChanged || refreshAsked) this.fetchVerdicts();
  }

  // Remontée vers le conteneur, qui a besoin de `isLoading` (pour PeriodSelector) et de `data`
  // (pour activer l'export). ⚠ Un React.createRef() ne marcherait PAS : ce composant est
  // démonté à chaque changement d'onglet, la ref serait nulle au retour.
  notifyState(next) {
    if (typeof this.props.onStateChange === 'function') this.props.onStateChange(next);
  }

  componentWillUnmount() {
    this._isMounted = false;
  }

  // Lecture seule : tout vient de CD1, le front affiche, il n'agrège pas.
  fetchVerdicts() {
    // ⛔ La fenêtre vient des PROPS, et de la paire APPLIQUÉE — pas de la saisie en cours.
    // Lire `from`/`to` (la saisie) rechargerait à chaque frappe dans le champ date.
    const { appliedFrom: from, appliedTo: to } = this.props;
    this.setState({ isLoading: true, error: null });
    this.notifyState({ data: this.state.data, isLoading: true, error: null });
    getVerdicts(from, to)
      .then((result) => {
        if (!this._isMounted) return;
        // ⚠ GARDE DÉFENSIVE — http.get ne lève que sur un statut non-2xx (services/http.js:9),
        // or l'API répond « HTTP 200 + { gcCode: 410 } » quand le JWT est absent ou expiré
        // (vérifié en live). Sans ce test, un token expiré entre le montage et un « Rafraîchir »
        // arriverait ici COMME DES DONNÉES, et l'écran afficherait un tableau vide au lieu de
        // dire que la session est tombée. withAuth ne couvre que le montage, pas les recharges.
        // ⚠ `err !== 'undefined'` n'est pas une coquetterie : l'API renvoie parfois la CHAÎNE
        // littérale 'undefined' dans ce champ. Même test que withAuth.jsx:34 et App.js:61 —
        // on s'aligne sur le précédent du repo plutôt que d'en inventer un second.
        const hasErr = result && result.err && result.err !== 'undefined';
        if (!result || typeof result !== 'object' || result.gcCode || hasErr) {
          const code = result && (result.gcCode || result.err);
          const msg = (result && result.msg) ? ` — ${result.msg}` : '';
          const authError = `session expirée ou accès refusé (code ${code})${msg}. Recharge la page pour te reconnecter.`;
          this.setState({ error: authError, isLoading: false });
          this.notifyState({ data: null, isLoading: false, error: authError });
          return;
        }
        this.setState({ data: result, isLoading: false });
        this.notifyState({ data: result, isLoading: false, error: null });
      })
      .catch((err) => {
        if (!this._isMounted) return;
        const message = (err && err.message) || 'Erreur de chargement';
        this.setState({ error: message, isLoading: false });
        this.notifyState({ data: null, isLoading: false, error: message });
      });
  }

  toggleRow(key) {
    this.setState((prev) => ({ expanded: { ...prev.expanded, [key]: !prev.expanded[key] } }));
  }

  // Fenêtre RÉELLEMENT calculée par le serveur, telle qu'il la renvoie (`data.window`) — jamais
  // l'état local `from`/`to`. Les deux divergent dès qu'on édite un champ date sans cliquer
  // « Rafraîchir », et quand une borne est vide le service l'omet de la query : l'endpoint
  // applique alors sa fenêtre de 90 j pendant que l'écran en nommerait une autre.
  windowLabel() {
    const { data } = this.state;
    const w = (data && data.window) || {};
    const day = (v) => {
      const d = v ? new Date(v) : null;
      return d && !Number.isNaN(d.getTime()) ? toInputDate(d) : null;
    };
    const f = day(w.from);
    const t = day(w.to);
    return f && t ? `entre le ${f} et le ${t}` : 'sur la période interrogée';
  }

  // Dénominateurs par DIRECTION. CD1+ n'expose que le dénominateur COMBINÉ (`denominator`) ; on
  // recompose ici les deux dénominateurs séparés en RÉPLIQUANT EXACTEMENT l'arithmétique du
  // contrôleur (`exports.verdicts`, bloc D28) — sinon l'écran afficherait un ratio incohérent
  // avec le `rate` que le serveur a lui-même calculé.
  //   laxisme     : dénominateur = verdicts OK (toutes les propositions « garder », y compris
  //                 celles jamais tranchées — D28 : elles comptent comme des accords tacites).
  //   sur-censure : dénominateur = verdicts KO (même règle).
  // ⚠ Un dénominateur à 0 est « NON MESURABLE », jamais « 0 % » : l'endpoint renvoie rate:0 dans
  // ce cas (`rate = (count, base) => base > 0 ? … : 0`), ce qui se lirait comme un sans-faute
  // alors qu'il n'y a rien à mesurer.
  denominators(row) {
    // Délègue à la fonction pure de module — l'export CSV consomme la MÊME.
    return computeDenominators(row);
  }

  // Une barre = une direction d'erreur. Encodage TRIPLE (couleur + icône + libellé) : la couleur
  // seule ne porte jamais l'information (contrainte d'accessibilité de la skill `dataviz`).
  //
  // ⚠ LE NOMBRE DE CAS EST TOUJOURS AFFICHÉ, même quand le taux n'est pas calculable. Jusqu'à
  // D28 (B1_04), une ligne pouvait porter `laxisme { count: 4, rate: 0 }` avec un dénominateur
  // nul — 4 commentaires que l'IA a eu tort de garder, et un taux à 0. Ce cas n'est plus
  // atteignable en nominal, mais masquer le compteur derrière un « non mesurable » ferait
  // disparaître de l'écran des erreurs réelles si le serveur redevenait incohérent —
  // exactement ce que ce dashboard existe pour empêcher.
  //
  // Trois états, jamais confondus :
  //   cas ≤ dénominateur, dénominateur > 0 → taux + (cas / dénominateur)
  //   dénominateur = 0 et 0 cas            → « non mesurable » : rien à mesurer, pas un sans-faute
  //   cas > dénominateur (0 compris)       → ANOMALIE : N cas réels, taux incalculable
  //
  // ⚠ LE SEUIL EST `cas > dénominateur`, PAS `dénominateur === 0`. Historique : jusqu'à D28
  // (B1_04), l'API retranchait du dénominateur des « gardés » TOUTES les suggestions jamais
  // tranchées, y compris celles de SUPPRESSION — le dénominateur passait sous le nombre de cas
  // sans devenir nul (OK 100, laxisme 80, 20 « garder » et 25 « supprimer » jamais tranchés →
  // dénominateur 55, rate 1,4545). Un seuil à `=== 0` aurait laissé l'écran afficher
  // « 100,0 % (80 cas sur 55) ». Depuis D28 le numérateur est un sous-ensemble du dénominateur
  // et ce cas n'est plus atteignable en nominal ; le seuil reste `cas > dénominateur` parce
  // que c'est le seul qui détecte un serveur redevenu incohérent.
  renderDirectionBar(direction, label, entry, denominator, Icon) {
    // ⛔ Les trois états viennent de directionState() — la MÊME fonction que l'export CSV.
    // Les recalculer ici ferait diverger l'écran et le fichier au premier ajustement (T1).
    const { state, count } = directionState(entry, denominator);
    const measurable = state === 'measurable';
    const inconsistent = state === 'inconsistent';
    const color = DIRECTION_COLOR[direction];

    return (
      <Box sx={{ mb: 1.5 }}>
        <Stack direction="row" alignItems="baseline" justifyContent="space-between" spacing={1} sx={{ mb: 0.25 }}>
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <Icon fontSize="small" color={color} />
            <Typography variant="body2">{label}</Typography>
          </Stack>
          <Typography variant="body2" color="text.secondary" data-testid={`monitoring-rate-${direction}`}>
            {measurable && (
              <>
                <Typography component="span" variant="subtitle2" color="text.primary">
                  {pct(entry && entry.rate)}
                </Typography>
                {' '}
                <Typography component="span" variant="caption">
                  ({nf.format(count)} cas sur {nf.format(denominator)})
                </Typography>
              </>
            )}
            {inconsistent && (
              <Tooltip title={`Le nombre de cas est fiable, le taux ne l’est pas : l’API annonce ${nf.format(denominator)} verdict(s) au dénominateur pour ${nf.format(count)} cas — incohérence côté serveur. Se fier au nombre, pas au pourcentage.`}>
                <Stack component="span" tabIndex={0} direction="row" alignItems="center" spacing={0.5} sx={{ display: 'inline-flex' }}>
                  <WarningAmberOutlinedIcon fontSize="inherit" color="warning" />
                  <Typography component="span" variant="subtitle2" color="text.primary">
                    {nf.format(count)} cas
                  </Typography>
                  <Typography component="span" variant="caption">
                    · {RATE_INCONSISTENT}
                  </Typography>
                </Stack>
              </Tooltip>
            )}
            {!measurable && !inconsistent && (
              <Tooltip title="Aucun verdict de ce type au dénominateur sur la période : il n’y a rien à mesurer. Ce n’est pas un taux de 0 %.">
                <Typography component="span" tabIndex={0} variant="caption" sx={{ fontStyle: 'italic' }}>
                  {RATE_UNMEASURABLE} ({nf.format(denominator)} verdict au dénominateur)
                </Typography>
              </Tooltip>
            )}
          </Typography>
        </Stack>
        {/* ⚠ La PISTE est forcée en gris neutre (`action.hover`), pas laissée au défaut MUI —
            qui la teinte d'une nuance pâle de la couleur de série. Vérifié à l'écran le
            2026-08-27 : avec le défaut, une barre à 0 % s'affiche PLEINE LARGEUR en ambre pâle
            et se lit comme une barre remplie, soit l'inverse exact de ce qu'elle mesure. Une
            piste grise rend un taux nul visiblement vide. */}
        <LinearProgress
          variant="determinate"
          value={measurable ? barValue(entry && entry.rate) : 0}
          color={color}
          sx={{
            height: 10,
            borderRadius: 1,
            bgcolor: 'action.hover',
            '& .MuiLinearProgress-bar': { borderRadius: 1 }
          }}
        />
      </Box>
    );
  }

  // Détail par motif. ⚠ `by_motif` ne ventile QUE LA SUR-CENSURE : le contrôleur n'y fait entrer
  // que les verdicts KO (« by_motif ventile les SUPPRESSIONS »), et `reversed` n'est incrémenté
  // que pour eux. Le laxisme n'existe qu'au niveau site × régime — c'est logique, un verdict OK
  // n'a pas de motif de suppression. Le dire à l'écran, sinon le tableau se lit de travers.
  renderMotifDetail(row) {
    const motifs = Array.isArray(row.by_motif) ? row.by_motif : [];
    const koWithoutMotif = Number(row.ko_without_motif) || 0;
    const totalKo = Number(row.verdicts && row.verdicts.KO) || 0;
    // Garde-fou de réconciliation : le contrôleur a été écrit pour que cette égalité tienne
    // (`ko_without_motif` existe précisément pour ça). Si elle casse, l'écran afficherait deux
    // totaux qui se contredisent — mieux vaut le dire que le masquer.
    const sumKo = motifs.reduce((acc, m) => acc + (Number(m.ko) || 0), 0) + koWithoutMotif;
    const reconciles = sumKo === totalKo;

    if (!motifs.length && !koWithoutMotif) {
      return (
        <Typography variant="body2" color="text.secondary" sx={{ py: 1.5 }}>
          Aucune suppression par l’IA sur cette période : il n’y a pas de motif à ventiler.
        </Typography>
      );
    }

    return (
      <Box sx={{ py: 1 }}>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          Ventilation des <strong>suppressions décidées par l’IA</strong> et de celles qu’un humain
          a rétablies. Le laxisme n’apparaît pas ici : un verdict « je laisse passer » ne porte
          aucun motif de suppression.
        </Typography>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Motif</TableCell>
                <TableCell align="right">Supprimés par l’IA</TableCell>
                <TableCell align="right">Rétablis par un humain</TableCell>
                <TableCell align="right">Part rétablie</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {motifs.map((m) => {
                const ko = Number(m.ko) || 0;
                const reversed = Number(m.reversed) || 0;
                const flagged = isOutOfTaxonomy(m.motif_id);
                return (
                  <TableRow key={String(m.motif_id)}>
                    <TableCell>
                      <Stack direction="row" alignItems="center" spacing={0.5}>
                        <Typography variant="body2">{motifLabel(m.motif_id)}</Typography>
                        {flagged && (
                          <Tooltip title="Motif produit par l’IA alors qu’il n’existe pas dans la taxonomie du produit : ni libellé back-office, ni poids de malus. C’est un défaut de prompt, pas un motif normal.">
                            <WarningAmberOutlinedIcon fontSize="inherit" color="warning" />
                          </Tooltip>
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell align="right">{nf.format(ko)}</TableCell>
                    <TableCell align="right">{nf.format(reversed)}</TableCell>
                    <TableCell align="right">
                      {ko > 0 ? pct(reversed / ko) : <em>non mesurable</em>}
                    </TableCell>
                  </TableRow>
                );
              })}
              {koWithoutMotif > 0 && (
                <TableRow>
                  <TableCell>
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                      <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                        Motif non renseigné
                      </Typography>
                      <Tooltip title="Verdict reconstitué depuis les journaux : le statut est connu, le motif ne l’est pas. Compté à part pour que le total de cette colonne corresponde au nombre de suppressions.">
                        <InfoOutlinedIcon fontSize="inherit" color="disabled" />
                      </Tooltip>
                    </Stack>
                  </TableCell>
                  <TableCell align="right">{nf.format(koWithoutMotif)}</TableCell>
                  <TableCell align="right">—</TableCell>
                  <TableCell align="right">—</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {!reconciles && (
          <Alert severity="warning" sx={{ mt: 1 }}>
            Incohérence de comptage : {nf.format(sumKo)} suppressions ventilées par motif contre{' '}
            {nf.format(totalKo)} annoncées au total. Le détail par motif est à prendre avec
            précaution sur cette ligne.
          </Alert>
        )}
      </Box>
    );
  }

  // Borne `to` réellement appliquée par le serveur (pour les libellés d'âge D59) — même
  // source que windowLabel() : `data.window`, jamais la saisie locale.
  windowToLabel() {
    const { data } = this.state;
    const t = data && data.window && data.window.to ? new Date(data.window.to) : null;
    return t && !Number.isNaN(t.getTime()) ? toInputDate(t) : null;
  }

  // B6_02 — les 4 catégories écartées de la ventilation par version (D34), en chips TOUJOURS
  // visibles hors dépliage : DA20 interdit de masquer « à cheval » dès que le compte est non
  // nul, et D26/D34 exigent les quatre visibles et distinctes. Forme par ligne : objets
  // `{count}` (⛔ la synthèse globale, elle, sert des NOMBRES NUS — deux lectures distinctes).
  renderVersionBadges(row) {
    if (!Array.isArray(row.by_version)) return null; // api d'avant B6_01
    const notes = (this.state.data && this.state.data.notes) || {};
    const straddling = Number(row.straddling && row.straddling.count) || 0;
    const windowMin = Number(row.straddling && row.straddling.window_minutes) || 0;
    const unstamped = Number(row.unstamped && row.unstamped.count) || 0;
    const unresolved = Number(row.unresolved && row.unresolved.count) || 0;
    const rejudged = Number(row.rejudged && row.rejudged.count) || 0;

    return (
      <>
        {straddling > 0 && (
          <Tooltip title={`Analyses rendues dans la fenêtre de bascule d’une publication faite depuis l’outil (±${nf.format(windowMin)} min) : la version qui a réellement jugé est incertaine. Écartées de la ventilation par version, jamais des volumes. Une modification faite hors de l’outil n’ouvre aucune fenêtre : sa date exacte est inconnue.`}>
            <Chip
              size="small"
              color="warning"
              variant="outlined"
              icon={<InfoOutlinedIcon />}
              label={`${nf.format(straddling)} à cheval sur deux versions (±${nf.format(windowMin)} min)`}
            />
          </Tooltip>
        )}
        {unstamped > 0 && (
          <Tooltip title="Analyses rendues avant le déploiement du suivi par version : elles ne portent aucune estampille et ne peuvent être attribuées à aucune version. Comptées dans les volumes, écartées de la ventilation.">
            <Chip
              size="small"
              variant="outlined"
              icon={<InfoOutlinedIcon />}
              label={`${nf.format(unstamped)} antérieures au suivi par version`}
            />
          </Tooltip>
        )}
        {unresolved > 0 && (
          // La note servie (B4_04 : `notes.unresolved_scope`) fait foi dès qu'elle existe —
          // le texte en dur n'est qu'un repli d'avant sa livraison.
          <Tooltip title={notes.unresolved_scope || 'L’estampille n’a pas pu être reliée à une version publiée (registre non amorcé ou en panne au moment du verdict), ou la période traverse une dérive détectée par la vérification quotidienne. Les fondre dans une autre catégorie masquerait une panne.'}>
            <Chip
              size="small"
              color="warning"
              variant="outlined"
              icon={<WarningAmberOutlinedIcon />}
              label={`${nf.format(unresolved)} version non résolue`}
            />
          </Tooltip>
        )}
        {/* D58 — la catégorie s'affiche AVEC la mention servie, jamais un « 0 » nu qui se
            lirait « aucune réanalyse » alors que la détection n'existe pas encore. */}
        <Tooltip title={notes.rejudged_detection || 'Détection des commentaires jugés deux fois non disponible.'}>
          <Chip
            size="small"
            variant="outlined"
            icon={<InfoOutlinedIcon />}
            label={rejudged > 0
              ? `${nf.format(rejudged)} jugés deux fois`
              : 'jugés deux fois : détection non disponible'}
          />
        </Tooltip>
      </>
    );
  }

  // B6_02 — tableau « une ligne par version » (mêmes colonnes pour le détail d'une carte et
  // la synthèse toutes communautés). Les taux passent par rateCell — le MÊME oracle des trois
  // états que la ligne et le CSV, appliqué à l'entrée par version (sa propre réplique).
  renderVersionTable(entries, toLabel, rowTestId) {
    const notes = (this.state.data && this.state.data.notes) || {};
    return (
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Consigne</TableCell>
              <TableCell>Version</TableCell>
              <TableCell>Publication</TableCell>
              <TableCell align="right">Décisions humaines</TableCell>
              <TableCell align="right">Analyses</TableCell>
              <TableCell align="right">Laxisme</TableCell>
              <TableCell align="right">Sur-censure</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {entries.map((entry) => {
              const vr = versionRow(entry);
              const den = computeDenominators(entry);
              const dis = entry.disagreement || {};
              const missing = registryMissing(entry);
              const diverge = supplierVersionsDiverge(entry);
              const pending = Number(dis.pending_human) || 0;
              const rowKey = `${entry.agent_id}|${entry.prompt_version}`;
              return (
                <TableRow key={rowKey} data-testid={rowTestId}>
                  <TableCell>
                    <Typography variant="body2">{consigneLabel(entry)}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {entry.prompt_version === null || entry.prompt_version === undefined
                        ? 'n° inconnu' : `n° ${entry.prompt_version}`}
                    </Typography>
                    {/* D35 — les DEUX numéros fournisseur, libellés distincts, divergence
                        signalée : le n° figé au verdict est le seul qui date du jugement, le
                        n° du registre est le seul qui se recoupe avec la console aujourd'hui. */}
                    <Tooltip title={
                      (entry.agent_version === null || entry.agent_version === undefined)
                        ? AGENT_VERSION_UNKNOWN_TOOLTIP
                        : (diverge
                          ? 'Les deux numéros divergent : la ligne de registre a été resynchronisée (ou retournée en arrière) depuis ce verdict. Le n° au verdict est le seul qui date du jugement ; le n° au registre est le seul qui se recoupe avec la console fournisseur aujourd’hui.'
                          : 'N° chez le fournisseur au moment du verdict (figé) · n° chez le fournisseur aujourd’hui (ligne de registre, réécrit en place par une resynchronisation).')
                    }>
                      <Typography component="span" tabIndex={0} variant="caption" color="text.secondary" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                        fournisseur : {agentVersionCell(entry)} au verdict · {mistralVersionCell(entry)} au registre
                        {/* Divergence dite en toutes lettres — jamais la couleur ou l'icône seule. */}
                        {diverge && (
                          <>
                            <WarningAmberOutlinedIcon fontSize="inherit" color="warning" />
                            divergents
                          </>
                        )}
                      </Typography>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    {missing ? (
                      <Tooltip title="Cette version apparaît dans les verdicts mais aucune ligne du registre ne lui correspond : date, auteur, origine et n° fournisseur sont introuvables. La ligne reste affichée — la masquer ferait disparaître des analyses réelles.">
                        <Typography component="span" tabIndex={0} variant="body2" sx={{ fontStyle: 'italic' }}>
                          {REGISTRY_MISSING_LABEL}
                        </Typography>
                      </Tooltip>
                    ) : (
                      <>
                        <Typography variant="body2">
                          {entry.published_at ? String(entry.published_at).slice(0, 10) : 'date inconnue'}
                          {entry.author && entry.author.username ? ` — ${entry.author.username}` : ' — auteur inconnu'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                          {publishedSinceLabel(entry.published_since_days, toLabel)}
                          {entry.origin === 'adopted' && ' · remise à niveau — date exacte du changement inconnue'}
                        </Typography>
                      </>
                    )}
                  </TableCell>
                  <TableCell align="right">
                    {entry.human_decisions === null || entry.human_decisions === undefined ? (
                      // D56 — null sur les lignes « appliqué » : un accord n'y laisse pas de
                      // trace. La mention servie (notes) fait foi, jamais un 0.
                      <Tooltip title={notes.human_decisions_applied_regime || HUMAN_DECISIONS_UNMEASURABLE}>
                        <Typography component="span" tabIndex={0} variant="caption" sx={{ fontStyle: 'italic' }}>
                          {HUMAN_DECISIONS_UNMEASURABLE}
                        </Typography>
                      </Tooltip>
                    ) : (
                      <Typography variant="body2">{nf.format(Number(entry.human_decisions) || 0)}</Typography>
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2">{nf.format(Number(entry.analyses) || 0)}</Typography>
                    {/* Remappage C2 : `vr.regime` = applied_regime du parent — jamais
                        agent_mode, qui éteindrait cette ventilation en silence. */}
                    {vr.regime === REGIME_SUGGESTED && pending > 0 && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        dont {nf.format(pending)} jamais tranchées
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="right">
                    {rateCell(dis.laxisme, den.laxisme, den.replicaMatches)}
                    {' '}· {nf.format(Number(dis.laxisme && dis.laxisme.count) || 0)} cas
                  </TableCell>
                  <TableCell align="right">
                    {rateCell(dis.sur_censure, den.sur_censure, den.replicaMatches)}
                    {' '}· {nf.format(Number(dis.sur_censure && dis.sur_censure.count) || 0)} cas
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    );
  }

  // B6_02 — rapprochements D35 : les deux dernières versions de chaque consigne, écart ou
  // refus motivé. §3 : aucun verdict de causalité — pas de couleur de jugement, pas de flèche.
  renderComparisons(entries) {
    const comparisons = versionComparisons(entries);
    if (!comparisons.length) return null;
    return (
      <Box sx={{ mt: 1 }}>
        {comparisons.map((cmp) => (
          <Typography key={`${cmp.recent.agent_id}|${cmp.recent.prompt_version}`} variant="body2" sx={{ mb: 0.25 }}>
            <strong>{consigneLabel(cmp.recent)}</strong> — {comparisonText(cmp)}
          </Typography>
        ))}
        <Typography variant="caption" color="text.secondary">
          {VERSION_FLOOR_NOTE}
        </Typography>
      </Box>
    );
  }

  // B6_02 — contenu du dépliage « par version » d'une carte site × régime.
  renderVersionDetail(row) {
    const entries = Array.isArray(row.by_version) ? row.by_version : [];
    if (!entries.length) {
      // ⛔ Pas un tableau de zéros, qui se lirait « zéro erreur » : un message d'attente.
      return (
        <Typography variant="body2" color="text.secondary" sx={{ py: 1.5 }}>
          Le suivi par version démarre au déploiement — aucune analyse estampillée sur cette
          période pour cette ligne.
        </Typography>
      );
    }
    const toLabel = this.windowToLabel();
    return (
      <Box sx={{ py: 1 }}>
        {/* testid distinct de celui de la synthèse : la recette asserte que CE tableau
            n'apparaît qu'une fois le Collapse ouvert. */}
        {this.renderVersionTable(entries, toLabel, 'monitoring-version-row')}
        {this.renderComparisons(entries)}
      </Box>
    );
  }

  // B6_02 / D33 + D57 + décision A — la synthèse « toutes communautés », UNE PAR RÉGIME, en
  // tête de sa section (jamais un bloc unique qui mélangerait deux mesures, DA12). Servie par
  // l'API (`by_version_global`) — ⛔ le front affiche, il n'agrège pas. Rendue
  // INCONDITIONNELLEMENT dès que l'API sert la clé, même à zéro (D57).
  renderGlobalSynthesis(regime) {
    const data = this.state.data || {};
    if (!Array.isArray(data.by_version_global)) return null; // api d'avant B6_01
    const entries = data.by_version_global.filter((e) => e.applied_regime === regime);
    const g = (Array.isArray(data.global_by_regime) ? data.global_by_regime : [])
      .find((r) => r.applied_regime === regime) || {};
    // ⛔ NOMBRES NUS ici (pas d'objets {count} — et pas de window_minutes : la durée de
    // fenêtre n'existe pas au niveau global, ne pas l'inventer).
    const straddling = Number(g.straddling) || 0;
    const unstamped = Number(g.unstamped) || 0;
    const unresolved = Number(g.unresolved) || 0;
    const rejudged = Number(g.rejudged) || 0;
    const notes = data.notes || {};
    const toLabel = this.windowToLabel();

    return (
      <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: 'action.hover' }} data-testid={`monitoring-version-global-${regime}`}>
        <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 0.5 }}>
          <Typography variant="subtitle2">Toutes communautés confondues — par version de consigne</Typography>
          <Chip size="small" label={`${nf.format(Number(g.analyses) || 0)} analyses`} />
        </Stack>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          Les consignes sont communes à tout le parc : cette synthèse est calculée et servie par
          l’API — l’écran n’additionne jamais les communautés lui-même. Avant / après
          observationnel : deux taux, deux volumes, un écart — pas une expérience contrôlée.
        </Typography>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
          {straddling > 0 && (
            <Tooltip title="Analyses rendues dans la fenêtre de bascule d’une publication faite depuis l’outil : la version qui a réellement jugé est incertaine. La durée de la fenêtre se lit sur les lignes par communauté — elle n’existe pas au niveau global.">
              <Chip size="small" color="warning" variant="outlined" label={`${nf.format(straddling)} à cheval sur deux versions`} />
            </Tooltip>
          )}
          {unstamped > 0 && (
            <Tooltip title="Analyses rendues avant le déploiement du suivi par version : aucune estampille, aucune attribution possible.">
              <Chip size="small" variant="outlined" label={`${nf.format(unstamped)} antérieures au suivi`} />
            </Tooltip>
          )}
          {unresolved > 0 && (
            <Tooltip title={notes.unresolved_scope || 'L’estampille n’a pas pu être reliée à une version publiée, ou la période traverse une dérive détectée par la vérification quotidienne.'}>
              <Chip size="small" color="warning" variant="outlined" label={`${nf.format(unresolved)} version non résolue`} />
            </Tooltip>
          )}
          <Tooltip title={notes.rejudged_detection || 'Détection des commentaires jugés deux fois non disponible.'}>
            <Chip
              size="small"
              variant="outlined"
              label={rejudged > 0 ? `${nf.format(rejudged)} jugées deux fois` : 'jugées deux fois : détection non disponible'}
            />
          </Tooltip>
        </Stack>
        {entries.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            Le suivi par version démarre au déploiement — aucune analyse estampillée toutes
            communautés confondues sur cette période.
          </Typography>
        ) : (
          <>
            {this.renderVersionTable(entries, toLabel, 'monitoring-version-global-row')}
            {this.renderComparisons(entries)}
          </>
        )}
      </Paper>
    );
  }

  // Une carte = une ligne site × régime de CD1.
  renderSiteRow(row, maturation) {
    const { expanded } = this.state;
    const key = `${row.website_id}|${row.regime}`;
    const isOpen = !!expanded[key];
    // B6_02 — clé PRÉFIXÉE : `expanded` est un espace partagé avec le dépliage motifs ;
    // sans le préfixe, les deux dépliages s'ouvriraient et se fermeraient ensemble.
    const versionKey = `version|${row.website_id}|${row.regime}`;
    const isVersionOpen = !!expanded[versionKey];
    const hasVersionData = Array.isArray(row.by_version); // api d'avant B6_01 → pas de bloc
    const ui = REGIME_UI[row.regime] || REGIME_UI[REGIME_APPLIED];
    const d = row.disagreement || {};
    const v = row.verdicts || {};
    const den = this.denominators(row);
    const fallbacks = Number(row.verdict_fallbacks) || 0;
    const pending = Number(d.pending_human) || 0;
    // CD1+ (B1_04) — ventilation par sens, servie par l'API (0 en régime appliqué). Absente
    // pendant la fenêtre de MEP (écran avant api) → le chip n'affiche que le total.
    const hasVentilation = hasPendingVentilation(d);
    const pendingOk = Number(d.pending_ok) || 0;
    const pendingKo = Number(d.pending_ko) || 0;

    return (
      <Paper key={key} variant="outlined" sx={{ p: 2, mb: 2 }} data-testid="monitoring-site-row">
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', md: 'center' }}
          spacing={1}
          sx={{ mb: 1.5 }}
        >
          <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
            <Typography variant="subtitle1">{siteLabel(row)}</Typography>
            {/* Lien croisé (décision Mathias L2, 2026-08-27) — vers l'ESPACE CLIENT du site.
                ⛔ Pas de `#` dans `to` : HashRouter le pose lui-même (AppRouter.jsx). Un
                `to={`#/…`}` produirait un lien mort — faille déjà payée sur B0_02.
                ⚠ Le back-office client a sa PROPRE session (clé localStorage distincte) : un
                écran de connexion peut apparaître. Ce n'est pas un bug de ce lien.
                ⚠ Aucune pré-sélection d'onglet n'est promise : Comment.js ne lit que
                `match.params.website_id`, le reste serait hors write-set. */}
            {row.website_id && (
              <Tooltip title="Ouvrir les commentaires de ce site dans l’espace client. Cet espace a sa propre session : un écran de connexion peut apparaître.">
                <Link
                  component={RouterLink}
                  to={`/super-admin/website/${row.website_id}/comment`}
                  variant="caption"
                  underline="hover"
                  data-testid="monitoring-site-link"
                  sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25 }}
                >
                  <OpenInNewOutlinedIcon sx={{ fontSize: 14 }} />
                  Voir les commentaires
                </Link>
              </Tooltip>
            )}
            {/* ⚠ llm_ai_type est le RÉGLAGE D'AUJOURD'HUI, lu sur la config du site — pas le mode
                en vigueur au moment des verdicts. Même piège que D15, transposé au mode : une
                ligne peut légitimement porter « suggestion » et un réglage « AI_ONLY ». */}
            <Tooltip title="Mode de modération réglé aujourd’hui sur ce site. Attention : ce n’est pas forcément le mode en vigueur au moment des verdicts comptés ci-dessous.">
              <Chip size="small" variant="outlined" label={`réglage actuel : ${row.llm_ai_type ? modeLabel(row.llm_ai_type) : 'inconnu'}`} />
            </Tooltip>
          </Stack>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {/* D10 — volume d'analyses, jamais un montant. */}
            <Tooltip title="Nombre de verdicts classables rendus par l’IA sur la période. Les analyses non classables (voir l’encart en tête d’écran) n’y sont pas comptées.">
              <Chip size="small" label={`${nf.format(Number(row.analyses) || 0)} analyses`} />
            </Tooltip>
            <Chip size="small" variant="outlined" label={`gardés ${nf.format(Number(v.OK) || 0)}`} />
            <Chip size="small" variant="outlined" label={`supprimés ${nf.format(Number(v.KO) || 0)}`} />
            <Tooltip title="L’IA n’a pas tranché et a renvoyé la décision à un humain. Ces verdicts comptent dans les volumes, jamais dans un taux : il n’y a rien à contredire.">
              <Chip size="small" variant="outlined" label={`sans décision ${nf.format(Number(v.REVIEW) || 0)}`} />
            </Tooltip>
          </Stack>
        </Stack>

        {this.renderDirectionBar('laxisme', ui.laxisme, d.laxisme, den.laxisme, VisibilityOffOutlinedIcon)}
        {this.renderDirectionBar('sur_censure', ui.sur_censure, d.sur_censure, den.sur_censure, BlockOutlinedIcon)}

        {!den.replicaMatches && (
          <Alert severity="warning" sx={{ mb: 1 }}>
            Les dénominateurs affichés ne correspondent plus à ceux annoncés par l’API
            ({nf.format(den.laxisme)} + {nf.format(den.sur_censure)} ≠{' '}
            {nf.format(den.serverDenominator)}) : le calcul a changé côté serveur. Les
            pourcentages de cette ligne ne sont pas fiables — s’en tenir au nombre de cas.
          </Alert>
        )}

        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
          {/* Objectif 4 — pending_human se lit À CÔTÉ du taux de désaccord : c'est lui qui dit
              dans quelle mesure le pourcentage est un plancher (D28 : ces suggestions comptent
              comme des accords tacites). Détaillé par sens (Q4, 2026-08-28) pour dire de quel
              côté le chiffre est optimiste. Sans objet en régime appliqué (l'endpoint sert 0). */}
          {row.regime === REGIME_SUGGESTED && (
            <Tooltip title={`Suggestions qu’aucun humain n’a jamais tranchées. Elles restent au dénominateur et comptent comme des accords tacites : plus elles sont nombreuses, plus le taux de désaccord affiché est un plancher optimiste.${hasVentilation ? ` Détail : ${nf.format(pendingOk)} où l’IA proposait de garder, ${nf.format(pendingKo)} de supprimer.` : ''}`}>
              <Chip
                size="small"
                color={pending > 0 ? 'warning' : 'default'}
                variant="outlined"
                icon={<InfoOutlinedIcon />}
                data-testid="monitoring-pending-chip"
                label={hasVentilation
                  ? `${nf.format(pending)} suggestions jamais tranchées (${nf.format(pendingOk)} garder · ${nf.format(pendingKo)} supprimer)`
                  : `${nf.format(pending)} suggestions jamais tranchées`}
              />
            </Tooltip>
          )}
          {/* Objectif 3(a) — badge « maturation », branché sur le bloc maturation de CD1 et non
              sur une constante recopiée. Uniquement sur le régime auquel il s'applique. */}
          {maturation.relevant && row.regime === maturation.applies_to && (
            <Tooltip title={maturation.note}>
              <Chip
                size="small"
                color="warning"
                variant="outlined"
                icon={<InfoOutlinedIcon />}
                label={`en cours de maturation (${maturation.days} j)`}
              />
            </Tooltip>
          )}
          {fallbacks > 0 && (
            <Tooltip title="Verdicts dont la réponse de l’IA n’était plus exploitable et qui ont été reconstitués depuis les journaux de modération. Le sens du verdict est fiable, son motif souvent perdu.">
              <Chip
                size="small"
                variant="outlined"
                icon={<InfoOutlinedIcon />}
                label={`${nf.format(fallbacks)} verdicts reconstitués`}
              />
            </Tooltip>
          )}
          {this.renderVersionBadges(row)}
        </Stack>

        <Divider sx={{ my: 1.5 }} />

        <Stack direction="row" alignItems="center" spacing={0.5}>
          <IconButton
            size="small"
            onClick={() => this.toggleRow(key)}
            aria-expanded={isOpen}
            aria-controls={`motifs-${row.website_id}-${row.regime}`}
            aria-label={isOpen ? 'Masquer le détail par motif' : 'Afficher le détail par motif'}
          >
            {isOpen ? <KeyboardArrowDownIcon /> : <KeyboardArrowRightIcon />}
          </IconButton>
          <Typography
            variant="body2"
            sx={{ cursor: 'pointer' }}
            onClick={() => this.toggleRow(key)}
          >
            Détail par motif
          </Typography>
        </Stack>
        <Collapse in={isOpen} unmountOnExit id={`motifs-${row.website_id}-${row.regime}`}>
          {this.renderMotifDetail(row)}
        </Collapse>

        {/* B6_02 — dépliage « par version de consigne », clé et aria-controls DISTINCTS du
            dépliage motifs. Rendu dans renderSiteRow : la recette compte dessus (les
            sélecteurs ne résolvent que la première occurrence, même carte exigée). */}
        {hasVersionData && (
          <>
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <IconButton
                size="small"
                onClick={() => this.toggleRow(versionKey)}
                aria-expanded={isVersionOpen}
                aria-controls={`versions-${row.website_id}-${row.regime}`}
                aria-label={isVersionOpen ? 'Masquer le détail par version de consigne' : 'Afficher le détail par version de consigne'}
                data-testid="monitoring-version-toggle"
              >
                {isVersionOpen ? <KeyboardArrowDownIcon /> : <KeyboardArrowRightIcon />}
              </IconButton>
              <Typography
                variant="body2"
                sx={{ cursor: 'pointer' }}
                onClick={() => this.toggleRow(versionKey)}
              >
                Détail par version de consigne
              </Typography>
            </Stack>
            <Collapse in={isVersionOpen} unmountOnExit id={`versions-${row.website_id}-${row.regime}`}>
              {this.renderVersionDetail(row)}
            </Collapse>
          </>
        )}
      </Paper>
    );
  }

  // Une section = un régime. Les DEUX sont toujours rendues, même vides : une absence
  // silencieuse laisserait croire que le régime n'existe pas, alors qu'en production c'est la
  // suggestion qui porte le plus gros volume (DA12).
  //
  // ⚠ ORDRE FIXE, jamais trié par volume. Un tri par volume ferait permuter les deux sections
  // d'un clic de raccourci à l'autre et l'utilisateur perdrait son repère. Ce que DA12 exige
  // est que la suggestion ne soit pas RELÉGUÉE, pas qu'elle soit première par accident de
  // données : elle est donc en tête par construction, et le régime qui porte réellement le plus
  // gros volume est signalé par un chip. (Relevé en review de code le 2026-08-27.)
  renderRegimeSection(regime, rows, maturation, opts) {
    const ui = REGIME_UI[regime];
    const total = rows.reduce((acc, r) => acc + (Number(r.analyses) || 0), 0);
    const { isDominant, hideEmptyAlert } = opts || {};

    return (
      <Box key={regime} sx={{ mb: 4 }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }} flexWrap="wrap" useFlexGap>
          <Typography variant="subtitle1">{ui.title}</Typography>
          <Chip size="small" label={`${nf.format(total)} analyses`} />
          {isDominant && (
            <Chip size="small" color="primary" variant="outlined" label="plus gros volume" />
          )}
          <Typography variant="caption" color="text.secondary">
            (régime au moment de la décision)
          </Typography>
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {ui.subtitle}
        </Typography>

        {/* B6_02 / décision A — la synthèse « toutes communautés » du régime, EN TÊTE de sa
            section : le chiffre global reste sous le titre qui dit ce qu'il mesure (DA12,
            jamais un bloc unique qui fusionnerait renversement et désaccord). */}
        {this.renderGlobalSynthesis(regime)}

        {rows.length === 0 ? (
          // Quand AUCUN site n'a produit de verdict, le bandeau global le dit déjà : répéter
          // l'explication dans chaque section empilerait trois alertes pour un seul fait.
          !hideEmptyAlert && (
            <Alert severity="info" icon={<InfoOutlinedIcon />}>
              {ui.empty}
            </Alert>
          )
        ) : (
          rows.map((row) => this.renderSiteRow(row, maturation))
        )}
      </Box>
    );
  }

  // Encart d'honnêteté du signal (objectif 3). Il dit ce que l'écran NE couvre PAS — périmètre,
  // date de référence de la période, analyses non classables, exclusion des gestes de masse.
  // Un tableau de bord qui tait ses angles morts donne l'impression de tout couvrir.
  renderHonestyNotes(data) {
    const unclassified = data.unclassified || {};
    const unclassifiedTotal = Number(unclassified.total) || 0;
    const threshold = data.notes && data.notes.mass_action_threshold;
    const basis = data.window && data.window.basis;
    // Une ligne dont le régime n'est ni « applied » ni « suggested » n'apparaît dans AUCUNE
    // section : elle sortirait de l'écran sans laisser de trace. Sur un tableau de bord dont
    // l'argument est qu'il montre ses angles morts, ce serait le comble.
    const rows = Array.isArray(data.by_site) ? data.by_site : [];
    const unknownRegime = rows.filter(
      (r) => r.regime !== REGIME_APPLIED && r.regime !== REGIME_SUGGESTED
    ).length;

    return (
      <Alert severity="info" icon={<InfoOutlinedIcon />} sx={{ mb: 3 }}>
        <AlertTitle>Ce que ces chiffres couvrent — et ce qu’ils ne couvrent pas</AlertTitle>
        <Box component="ul" sx={{ pl: 2.5, m: 0 }}>
          <li>
            Signal mesuré <strong>uniquement sur les sites où notre IA tourne</strong>. Les sites
            confiés à un prestataire de modération externe n’y figurent pas.
          </li>
          {/* ⚠ Rendu dès que `basis` est présent, jamais conditionné à SA VALEUR : si l'API
              change un jour de date de référence, la mention doit afficher la nouvelle base,
              pas disparaître en silence — ce serait perdre l'objectif 3(d) sans prévenir. */}
          {basis && (
            <li>
              {basis === 'comment_created_at' ? (
                <>
                  La période porte sur la <strong>date de publication du commentaire</strong>, pas
                  sur la date du verdict — le régime suggestion n’horodate aucun verdict.
                </>
              ) : (
                <>
                  La période porte sur la date de référence <strong>{basis}</strong> (et non plus
                  sur la date de publication du commentaire).
                </>
              )}
            </li>
          )}
          <li>
            <strong>Bascule de juillet 2026</strong> : avant la mise en production, tous les sites
            appliquaient les verdicts ; depuis, ils suggèrent, sauf ceux armés le 24/07. Une
            période à cheval sur cette date mélange donc deux mondes — d’où la séparation stricte
            des deux régimes ci-dessous.
          </li>
          {unclassifiedTotal > 0 && (
            <li>
              <strong>{nf.format(unclassifiedTotal)} analyses non classables</strong> sont exclues
              de tous les chiffres ci-dessous ({unclassified.reason}).
              {Array.isArray(unclassified.by_site) && unclassified.by_site.length > 0 && (
                <>
                  {' '}Réparties sur :{' '}
                  {unclassified.by_site
                    .map((s) => `${s.name || s.website_id} (${nf.format(Number(s.count) || 0)})`)
                    .join(', ')}
                  .
                </>
              )}
            </li>
          )}
          {threshold ? (
            <li>
              Les <strong>gestes de masse</strong> sont exclus des renversements : à partir de{' '}
              {nf.format(threshold)} actions identiques dans la même seconde, on considère un
              nettoyage groupé, pas une correction de l’IA.
            </li>
          ) : null}
          {unknownRegime > 0 && (
            <li>
              <strong>
                {nf.format(unknownRegime)} ligne{unknownRegime > 1 ? 's' : ''} de régime inconnu
              </strong>{' '}
              ne {unknownRegime > 1 ? 'sont' : 'est'} affichée{unknownRegime > 1 ? 's' : ''} dans
              aucune des deux sections ci-dessous : l’API a renvoyé un régime que cet écran ne
              sait pas nommer.
            </li>
          )}
          <li>
            <strong>En régime suggestion, le pourcentage est un plancher.</strong>{' '}
            Les suggestions jamais tranchées (compteur « garder · supprimer » de chaque ligne)
            sont comptées comme des accords tacites, dans les deux directions : le taux réel de
            désaccord est <strong>au moins</strong> celui affiché. Les nombres de cas, eux, sont
            exacts.
          </li>
          <li>
            <strong>Méthode de calcul modifiée</strong> ({METHOD_CHANGE_LABEL}) : le dénominateur
            est désormais le total des propositions de l’IA. Les exports produits avant cette
            version ne sont pas comparables à ceux-ci.
          </li>
          {/* B6_02 — les mentions d'honnêteté servies par l'API (CD13), affichées VERBATIM :
              la donnée porte le texte, l'écran le rend (pattern mass_action_threshold).
              ⛔ Liste OUVERTE (toute clé textuelle), pas une liste blanche : B4_04 ajoute
              déjà une 6ᵉ note — une liste fermée la ferait disparaître en silence. */}
          {Object.keys(data.notes || {}).map((k) => {
            const text = data.notes[k];
            return typeof text === 'string' && text ? <li key={k}>{text}</li> : null;
          })}
        </Box>
      </Alert>
    );
  }

  render() {
    const { data, isLoading, error } = this.state;
    // ⚠ by_site est DÉJÀ trié par analyses décroissant côté serveur, et by_motif par nombre de
    // suppressions décroissant — ne pas re-trier ici, ce serait défaire un ordre voulu.
    const bySite = data && Array.isArray(data.by_site) ? data.by_site : [];
    const applied = bySite.filter((r) => r.regime === REGIME_APPLIED);
    const suggested = bySite.filter((r) => r.regime === REGIME_SUGGESTED);

    // Maturation : le taux « mûrit » avec 30 j de retard, un verdict récent n'a pas encore eu
    // toute sa fenêtre pour être renversé. On lit la fenêtre et sa cible DANS la réponse
    // (bloc `maturation` de CD1) plutôt que de recopier la constante côté front.
    const mat = (data && data.maturation) || {};
    const matDays = Number(mat.reversal_window_days) || 0;
    const windowTo = data && data.window && data.window.to ? new Date(data.window.to) : null;
    const maturation = {
      days: matDays,
      applies_to: mat.applies_to,
      note: mat.note || '',
      // Le badge n'a de sens que si la période mord réellement sur les 30 derniers jours.
      relevant: !!(matDays && windowTo && windowTo.getTime() > Date.now() - matDays * DAY_MS)
    };

    // Ordre FIXE (cf. renderRegimeSection) : la suggestion en tête par construction, le régime
    // réellement dominant signalé par un chip. L'ordre ne dépend jamais des données.
    const totalOf = (rows) => rows.reduce((acc, r) => acc + (Number(r.analyses) || 0), 0);
    const dominant = totalOf(suggested) >= totalOf(applied) ? REGIME_SUGGESTED : REGIME_APPLIED;
    const globalEmpty = bySite.length === 0;
    const sections = [[REGIME_SUGGESTED, suggested], [REGIME_APPLIED, applied]];

    return (
      <Box>
        {/* Le Stack qui vivait ici n'avait de sens que tant qu'il partageait la ligne avec
            PeriodSelector — celui-ci est remonté dans le conteneur (B3_01). */}
        <Typography variant="subtitle1" sx={{ mb: 2 }}>Où l’IA se trompe</Typography>

        {isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        )}

        {!isLoading && error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            Impossible de charger les verdicts : {error}
          </Alert>
        )}

        {!isLoading && !error && data && (
          <>
            {this.renderHonestyNotes(data)}

            {globalEmpty && (
              <Alert severity="warning" sx={{ mb: 3 }}>
                <AlertTitle>Aucun verdict de l’IA sur cette période</AlertTitle>
                {/* ⚠ La période citée vient de `data.window`, PAS de l'état local from/to.
                    L'édition manuelle d'un champ date ne recharge pas (il faut « Rafraîchir »),
                    et le service omet une borne vide — l'endpoint applique alors SA fenêtre par
                    défaut. Citer l'état local ferait donc affirmer un fait sur une période qui
                    n'a jamais été interrogée. */}
                Aucun site n’a produit de verdict {this.windowLabel()}. Élargis la période, ou
                vérifie que la modération IA est bien active quelque part.
              </Alert>
            )}

            {sections.map(([regime, rows]) => this.renderRegimeSection(regime, rows, maturation, {
              isDominant: regime === dominant && !globalEmpty,
              hideEmptyAlert: globalEmpty
            }))}
          </>
        )}
      </Box>
    );
  }
}

export default Monitoring;
